import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, ArrowUpRight, Check } from "lucide-react";
import { scrollToId } from "@/lib/api";

export default function Hero({ settings }) {
  const reduce = useReducedMotion();

  const title = settings.hero_title || "Un seul contact. Plusieurs savoir-faire.";
  const lines = title.split(/(?<=\.)\s+/);
  const chips = (settings.hero_subtitle || "").split(/[·•]/).map((c) => c.trim()).filter(Boolean);

  const fade = (delay) => ({
    initial: reduce ? false : { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.7, delay, ease: [0.16, 1, 0.3, 1] },
  });

  const TextBlock = (
    <>
      <motion.p {...fade(0)} className="flex items-center gap-2.5 font-mono text-xs tracking-[0.22em] uppercase text-pine" data-testid="hero-badge">
        <span className="hidden sm:block h-px w-8 shrink-0 bg-terra" aria-hidden="true" />
        <span className="sm:hidden h-1.5 w-1.5 shrink-0 rounded-full bg-terra" aria-hidden="true" />
        {settings.hero_kicker || "Particuliers · Professionnels"}
      </motion.p>

      <h1 className="mt-6 font-display font-semibold tracking-tight leading-[1.07] sm:leading-[1.04] text-[2rem] sm:text-5xl xl:text-[4rem]" data-testid="hero-title">
        {lines.map((line, i) => (
          <span key={i} className={`block overflow-hidden pb-1 ${i > 0 ? "text-terra" : "text-ink"}`}>
            <motion.span
              className="block"
              initial={reduce ? false : { y: "110%" }}
              animate={{ y: 0 }}
              transition={{ duration: 0.85, delay: 0.12 + i * 0.13, ease: [0.16, 1, 0.3, 1] }}
            >
              {line}
            </motion.span>
          </span>
        ))}
      </h1>

      {chips.length > 0 && (
        <motion.p {...fade(0.45)} className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-2 max-w-[15rem] md:max-w-none font-mono text-[11px] sm:text-xs uppercase tracking-[0.16em] text-body2 leading-relaxed" data-testid="hero-subtitle">
          {chips.map((c) => (
            <span key={c} className="inline-flex items-center gap-1.5 whitespace-nowrap">
              <Check className="h-3.5 w-3.5 shrink-0 text-terra" strokeWidth={3.5} aria-hidden="true" />
              {c}
            </span>
          ))}
        </motion.p>
      )}

      <motion.div {...fade(0.55)} className="mt-9 flex flex-row flex-wrap items-center gap-x-3 gap-y-3 sm:gap-x-7">
        <a
          href="#contact"
          onClick={(e) => { e.preventDefault(); scrollToId("#contact"); }}
          data-testid="hero-quote-cta"
          className="cta-signature w-[58%] min-w-[170px] h-[54px] px-4 text-sm sm:w-auto sm:min-w-0 sm:h-auto sm:px-7 sm:text-base"
        >
          {settings.hero_cta || "Demander un devis"}
          <span className="cta-arrow"><ArrowRight className="h-4 w-4" /></span>
        </a>
        <a
          href="#realisations"
          onClick={(e) => { e.preventDefault(); scrollToId("#realisations"); }}
          data-testid="hero-work-cta"
          className="group inline-flex items-center gap-1.5 whitespace-nowrap text-forest font-semibold text-xs sm:text-base underline underline-offset-[6px] decoration-terra/50 transition-colors hover:decoration-terra max-md:basis-full max-md:justify-center max-md:no-underline max-md:mt-1 max-md:rounded-full max-md:border max-md:border-forest/40 max-md:bg-white/30 max-md:py-3 max-md:px-5 max-md:text-sm"
        >
          Voir nos réalisations
          <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </a>
      </motion.div>
    </>
  );

  return (
    <section id="top" data-testid="hero-section" className="bg-cream overflow-x-clip">
      {/* ---- Mobile (<768px) : photo portrait dédiée en fond, layout contraint (container centré) ---- */}
      <div className="md:hidden" data-testid="hero-mobile">
        <div className="max-w-7xl mx-auto px-4">
          <div
            className="relative mt-20 mb-4 min-h-[calc(100svh-6.5rem)] rounded-[1.5rem] overflow-hidden bg-cover bg-[position:center_18%]"
            style={{ backgroundImage: "url('/hero-bg-mobile.jpg')" }}
          >
            <div className="px-5 pt-10 pb-8">{TextBlock}</div>
          </div>
        </div>
      </div>

      {/* ---- Desktop (>=768px) : image en background dans un layout contraint (container centré max-w-7xl) ---- */}
      <div className="hidden md:block" data-testid="hero-split">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div
            className="relative mt-20 mb-4 h-[calc(100vh-6.5rem)] rounded-[2rem] overflow-hidden bg-cover bg-[position:25%_center] lg:bg-[position:right_center]"
            style={{ backgroundImage: "url('/hero-bg.jpg')" }}
          >
            <div className="grid grid-cols-2 h-full">
              <div className="flex items-center pl-8 lg:pl-14 pr-6">
                <div className="w-full max-w-xl">{TextBlock}</div>
              </div>
              <div aria-hidden="true" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
